<template>
  <div id="LKPublicationsFormdataTable__scrollArea" :class="wrapperClasses">
    <BTable
      :list="list"
      :headers="headers"
      :actions="tableActions"
      :hide-default-header="sheet.vertical"
      :items-per-page-options="[-1]"
      mobile-breakpoint="200"
      :skeleton="skeleton"
      :loading="loading"
      class="LKPublicationsDataCollectTable"
      v-on="$listeners"
    >
      <template v-for="{ slot, value } in headers" #[slot]="{ header }">
        <LKPublicationsFormdataTableHeader :ref="slot" :key="value" :header="header" />
      </template>

      <template v-for="{ item } in headers" #[item.slot]="props">
        <LKPublicationsFormdataTableInput
          v-bind="props"
          :key="item.slot"
          :all_cells="all_cells"
          :headers="headers"
          :disabled="readonly"
          :vertical-line-data="verticalLineData"
          :vertical-cells-data="verticalCellsData[props.header.$cell.$index]"
          class="is-cell"
          @change="$emit('action')"
        />
      </template>

      <template v-if="sheet.vertical" #body="{ items }">
        <tr>
          <th class="LKPublicationsDataCollectTable__top LKPublicationsDataCollectTable__header-left">
            #
          </th>
          <template v-for="(item, index) in items">
            <td :key="index" class="LKPublicationsDataCollectTable__top">
              <template v-for="action in tableActions">
                <BTableButtonAction
                  :key="action.action"
                  :action="action"
                  :index="index"
                  :data="item"
                  :list="list"
                  v-on="$listeners"
                />
              </template>
            </td>
          </template>
        </tr>

        <template v-for="header in headers">
          <tr :key="header.value">
            <th class="LKPublicationsDataCollectTable__header-left">
              <LKPublicationsFormdataTableHeader :key="header.value" :header="header" />
            </th>

            <template v-for="(item, index) in items">
              <td :key="index">
                <LKPublicationsFormdataTableInput
                  :headers="headers"
                  :header="header"
                  :data="item"
                  :index="index"
                  :all_cells="all_cells"
                  :disabled="readonly"
                  :vertical-line-data="verticalLineData"
                  :vertical-cells-data="verticalCellsData[header.$cell.$index]"
                  class="is-cell"
                  @change="$emit('action')"
                />
              </td>
            </template>
          </tr>
        </template>
      </template>
      <template v-else-if="list.length" #body>
        <tbody
          id="LKPublicationsFormdataTable__contentArea"
          class="clusterize-content"
          :class="{ 'is-dialog-loading': isEditDialogLoading }"
        />
      </template>
    </BTable>

    <div v-if="expanded" class="collapse-wrapper">
      <v-tooltip left>
        <template #activator="{ on }">
          <v-btn icon outlined color="primary" v-on="on" @click.stop="$emit('table:collapse')">
            <v-icon>
              mdi-arrow-collapse
            </v-icon>
          </v-btn>
        </template>
        <span>Свернуть таблицу</span>
      </v-tooltip>
    </div>
  </div>
</template>

<script>
// import { EXCEL_CELL_ERROR_LABEL } from '@/helpers/enums/params'
import { isEmpty } from 'lodash'
import { dateFormatterIgnoreTz } from '@/helpers/dateFormatter'
import { SheetCellTypes, SheetCellTypeMethods } from '@/typings/api/FormTemplate'
import { createLKPublicationsFormdataTableEditModal } from '@/views/LKPublications/formdata/utils'
import Clusterize from 'clusterize.js'
import { FixClusterize } from './utils'
import { createPopper } from '@popperjs/core'

import BTable, { BTableButtonAction } from '@/components/common/BTable.vue'
import LKPublicationsFormdataTableHeader from './LKPublicationsFormdataTableHeader.vue'
import LKPublicationsFormdataTableInput from './LKPublicationsFormdataTableInput.vue'
import { ADD, DELETE, TableActionsTypes } from '@/helpers/enums/table-actions'

// eslint-disable-next-line func-names
Clusterize.prototype.getClusterNum = function () {
  this.options.scroll_top = this.scroll_elem.scrollTop
  return Math.floor((this.options.scroll_top + Math.max(this.scroll_elem.offsetHeight - this.options.block_height, 0))
    / (this.options.cluster_height - this.options.block_height)) || 0
}


FixClusterize(Clusterize)

const getHeaderInfo = (header/* , headers */) => {
  const { $cell: data } = header
  const slot = `header.${header.value}`

  let size = '16px'
  let text = data.description
  let icon = void 0

  switch (data.type) {
    case SheetCellTypes.number:
      text = text || 'Число'

      if (data.type_method === SheetCellTypeMethods.number_interval) {
        const message = `Число должно быть между ${data.number_from} и ${data.number_to}`
        text = [data.description, message].filter(Boolean).join('<br>')
      }

      icon = 'mdi-numeric-9-plus-box-multiple-outline'
      break

    case SheetCellTypes.string: {
      text = text || (header.$cell.string_long ? 'Текст' : 'Строка')

      const min = data.string_min
      const max = data.string_max

      if (min || max) {
        // eslint-disable-next-line no-nested-ternary
        const message = min && max
          ? `Строка должно быть между ${min} и ${max} символами`
          : min
            ? `Строка должно больше ${min} символов`
            : `Строка должно быть меньше ${max} символов`

        text = [data.description, message].filter(Boolean).join('<br>')
      }

      icon = 'mdi-card-text-outline'
      break
    }

    case SheetCellTypes.date:
      text = text || 'Дата'
      icon = 'mdi-calendar'

      if (data.type_method === SheetCellTypeMethods.date_interval) {
        const from = dateFormatterIgnoreTz(data.date_from, 'D.MM.YYYY')
        const to = dateFormatterIgnoreTz(data.date_to, 'D.MM.YYYY')
        const message = `Дата должна быть между ${from} и ${to}`
        text = [data.description, message].filter(Boolean).join('<br>')
      }
      break

    case SheetCellTypes.time:
      text = text || 'Время'
      icon = 'mdi-clock'

      break

    case SheetCellTypes.formula: {
      /*
      const formulaMap = headers.reduce((acc, _) => {
        acc[_.$cell.uuid] = _.$cell.code
        return acc
      }, {})

      const formula = data.formula?.replace(
        /\[#\$_(.+?)#\]/g,
        (match, uuid) => formulaMap[uuid] || EXCEL_CELL_ERROR_LABEL,
      )
      */

      size = '20px'
      // text = text || `Формула:<br>${formula}`
      icon = 'mdi-music-accidental-sharp'
      break
    }

    default:
  }

  switch (data.type_method) {
    case SheetCellTypeMethods.auto_start:
      text = text || 'Авто'
      icon = 'mdi-arrow-down'
      break

    default:
  }

  // eslint-disable-next-line object-curly-newline
  return text && { text, icon, size, slot }
}

const getHeaderItem = (header) => {
  const { $cell: data } = header
  const slot = `item.${header.value}`
  let component = LKPublicationsFormdataTableInput

  switch (data.type) {
    case SheetCellTypes.date_interval:
    case SheetCellTypes.number_interval:
    case SheetCellTypes.list_interval:
      // component = null
      break

    case SheetCellTypes.number:
    case SheetCellTypes.string:
    case SheetCellTypes.date:
    case SheetCellTypes.formula:
    case SheetCellTypes.list_cell:
      component = LKPublicationsFormdataTableInput
      break

    default:
  }

  return component && { slot }
}

export default {
  name: 'LKPublicationsFormdataTable',
  components: {
    BTable,
    BTableButtonAction,
    LKPublicationsFormdataTableHeader,
    LKPublicationsFormdataTableInput,
  },
  props: {
    loading: Boolean,
    skeleton: Boolean,
    readonly: Boolean,
    canEdit: Boolean,
    expanded: Boolean,
    all_cells: {
      type: Array,
      required: true,
    },
    sheet: {
      type: Object,
      required: true,
      validator: (prop) => (
        prop
        && 'cells' in prop
        && 'vertical' in prop
      ),
    },
    list: {
      type: Array,
      required: true,
    },
    errors: {
      type: Object,
    },
    actions: {
      type: Array,
      required: true,
    },
    verticalLineData: {
      type: Object,
      default: () => { },
    },
    shouldUpdate: {
      type: Boolean,
    },
    scheduleInformation: {
      type: Object,
    },
  },
  data: () => ({
    clusterizeObj: null,
    isEditDialogLoading: false,
  }),
  computed: {
    headers() {
      const headers = this.sheet.cells.map(($cell, cell_uuid) => {
        const text = $cell.name || $cell.code
        return {
          $cell,
          text,
          hint: $cell.hint,
          value: `${cell_uuid}`,
          cell_uuid,
          sortable: false,
          slot: `header.${cell_uuid}`,
          width: $cell.width ? `${$cell.width}px` : null,
        }
      })

      headers.forEach((header) => {
        // eslint-disable-next-line no-param-reassign
        header.info = getHeaderInfo(header, headers)
        // eslint-disable-next-line no-param-reassign
        header.item = getHeaderItem(header)
      })

      return headers
    },
    hasPrefilledRows() {
      return typeof this.sheet.$prefilled_data !== 'undefined' && this.sheet.$prefilled_data !== null
    },
    tableActions() {
      return this.canEdit && !this.readonly && !this.hasPrefilledRows ? this.actions : []
    },
    actionsColumnWidth() {
      // Button width (28px * count) + left padding (8px) + right padding (8px) + right border (1px).
      return isEmpty(this.tableActions) ? 'unset' : `${this.tableActions.length * 28 + 17}px`
    },
    verticalCellsData() {
      const data = {}
      this.headers.forEach(({ $cell }) => {
        if ($cell.unique) {
          data[$cell.$index] = this.list.map((row) => row[$cell.$index]).filter((v) => typeof v !== 'undefined' && v !== null)
        }
      })
      if (!this.shouldUpdate) return data
      return data
    },
    wrapperClasses() {
      const classes = ['clusterize-scroll']
      if (this.expanded) {
        classes.push('expanded')
      }
      return classes.join(' ')
    },
  },
  watch: {
    shouldUpdate: {
      immediate: true,
      handler(value) {
        if (value) this.updateClusterizeObj()
      },
    },
    errors: {
      deep: true,
      handler() {
        this.updateClusterizeErrors()
      },
    },
    'sheet.code': {
      async handler() {
        await this.recreateTable()
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    this.destroyTable()
  },
  methods: {
    async destroyTable() {
      await this.$nextTick(() => {
        if (this.clusterizeObj) {
          const contentArea = document.querySelector('#LKPublicationsFormdataTable__contentArea')
          if (contentArea) {
            contentArea.removeEventListener('mousemove', this.clusterizeOnMouseMoveHandler)
            contentArea.removeEventListener('click', this.clusterizeOnClickHandler)
          }
          this.clusterizeObj.destroy()
        }
      })
    },
    async createTable() {
      await this.$nextTick()

      // Если прокрутить табличку в самый низ, находясь на листе с 100+ строками,
      // а после переключиться на другой лист с 20 строками, то окажемся в какой-то жопе.
      // Поэтому сперва ищем элемент и скроллим в (0, 0)
      const scrollArea = document.querySelector('#LKPublicationsFormdataTable__scrollArea')
      scrollArea?.scrollTo(0, 0)

      // eslint-disable-next-line no-new
      this.clusterizeObj = new Clusterize({
        rows: this.list.map(this.renderRowData),
        scrollId: 'LKPublicationsFormdataTable__scrollArea',
        contentId: 'LKPublicationsFormdataTable__contentArea',
        rows_in_block: 12,
      })

      const contentArea = document.querySelector('#LKPublicationsFormdataTable__contentArea')

      if (contentArea) {
        contentArea.removeEventListener('mousemove', this.clusterizeOnMouseMoveHandler)
        contentArea.removeEventListener('click', this.clusterizeOnClickHandler)

        contentArea.addEventListener('mousemove', this.clusterizeOnMouseMoveHandler)
        contentArea.addEventListener('click', this.clusterizeOnClickHandler)
      }
    },
    async recreateTable() {
      await this.destroyTable()
      await this.createTable()
    },
    clusterizeOnMouseMoveHandler({ target, clientX }) {
      if (target?.classList.contains('LKPublicationsFormdataTable__col') && target.dataset?.error) {
        const table = target.closest('table')

        let tableFooter = table.querySelector('tfoot')
        if (!tableFooter) {
          tableFooter = document.createElement('tfoot')
          table.appendChild(tableFooter)
        }

        const targetPaddingLeft = parseInt(window.getComputedStyle(target, null).getPropertyValue('padding-left'), 10)
        const targetBounds = target.getBoundingClientRect()
        if (targetBounds.right - targetPaddingLeft - 20 > clientX) {
          const tooltips = tableFooter.querySelectorAll('.LKPublicationsFormdataTable__error-tooltip')
          tooltips.forEach((t) => t.remove())
          return
        }

        const trIndex = target.closest('tr').dataset.index
        const tdIndex = target.dataset.index
        let tooltip = tableFooter.querySelector(`.LKPublicationsFormdataTable__error-tooltip[data-id="tooltip-${trIndex}-${tdIndex}"]`)
        if (!tooltip) {
          const tooltips = tableFooter.querySelectorAll(`.LKPublicationsFormdataTable__error-tooltip:not([data-id="tooltip-${trIndex}-${tdIndex}"])`)
          tooltips.forEach((t) => t.remove())
          tooltip = document.createElement('div')
          tooltip.classList.add('v-tooltip__content', 'LKPublicationsFormdataTable__error-tooltip')
          tooltip.dataset.id = `tooltip-${trIndex}-${tdIndex}`
          tableFooter.appendChild(tooltip)
        }
        tooltip.innerText = target.dataset?.error

        if (!tooltip.popperObj) {
          tooltip.popperObj = createPopper(target, tooltip, {
            placement: 'top',
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 0],
                },
              },
            ],
          })
        }
      } else {
        const table = target.closest('table')
        if (table) {
          const tooltips = table.querySelectorAll('tfoot .LKPublicationsFormdataTable__error-tooltip')
          tooltips.forEach((t) => t.remove())
        }
      }
    },
    clusterizeOnClickHandler({ target }) {
    // eslint-disable-next-line object-curly-newline
      const { onAdd, onEdit, onDelete, disabled, canEdit } = this
      if (disabled || !canEdit) return
      if (target?.classList.contains('LKPublicationsFormdataTable__col') || target?.classList.contains('LKPublicationsFormdataTable__cell')) {
        const row = target.closest('.LKPublicationsFormdataTable__row')
        const index = parseInt(row.dataset.index, 10)
        onEdit(this.list[index], index)
      } else if (target?.classList.contains('LKPublicationsFormdataTable__action-button--add')) {
        onAdd()
      } else if (target?.classList.contains('LKPublicationsFormdataTable__action-button--delete')) {
        onDelete(parseInt(target.dataset.index, 10))
      }
    },
    renderRowData(item, rowIndex) {
      let rowHtml = ''
      const colErrors = {}
      if (this.errors) {
        Object.keys(this.errors).forEach((errorMsg) => {
          if (this.errors[errorMsg][rowIndex]) {
            this.errors[errorMsg][rowIndex].forEach((col) => { colErrors[col] = errorMsg })
          }
        })
      }
      Object.values(this.headers).forEach((header, col) => {
        let error = ''
        let errorClass = ''
        let text
        let fullText = ''
        if (typeof item[header.cell_uuid] === 'undefined' || item[header.cell_uuid] === null) {
          text = '---'
        } else {
          text = item[header.cell_uuid]
          fullText = typeof text === 'string' ? text : String(text)
        }
        if (colErrors[col]) {
          error = ` data-error="${colErrors[col]}"`
          errorClass = ' error--text'
        }
        const titleAttr = fullText ? ` title="${fullText.replace(/"/g, '&quot;')}"` : ''
        rowHtml += `<td class="LKPublicationsFormdataTable__col${errorClass}" data-index="${col}"${error}><span class="LKPublicationsFormdataTable__cell"${titleAttr}>${text}</span></td>`
      })

      if (!this.disabled && this.canEdit) {
        rowHtml += this.renderActionButtons(item, rowIndex)
      }

      const canEdit = this.canEdit ? ' editable' : ''

      return `<tr class="LKPublicationsFormdataTable__row${canEdit}" data-index="${rowIndex}">${rowHtml}</tr>`
    },
    renderActionButtons(item, index) {
      const btns = []
      if (this.tableActions.some((o) => o.action === ADD.action) && this.list.length === index + 1) {
        btns.push(
          '<button type="button" class="LKPublicationsFormdataTable__action-button--add v-btn v-btn--icon v-btn--round v-btn--tile theme--light v-size--small"><span class="v-btn__content"><i aria-hidden="true" class="v-icon notranslate mdi mdi-plus theme--light" style="font-size: 16px;"></i></span></button>',
        )
      }

      if (this.tableActions.some((o) => o.action === DELETE.action) && (!isEmpty(item) || (this.list.length > 1))) {
        btns.push(
          `<button type="button" data-index="${index}" class="LKPublicationsFormdataTable__action-button--delete v-btn v-btn--icon v-btn--round v-btn--tile theme--light v-size--small error--text"><span class="v-btn__content"><i aria-hidden="true" class="v-icon notranslate mdi mdi-delete theme--light" style="font-size: 16px;"></i></span></button>`,
        )
      }

      return btns.length ? `<td><div class="d-flex flex-nowrap">${btns.join('')}</div></td>` : ''
    },
    async onEdit(item, index) {
      if (this.isEditDialogLoading) return
      this.isEditDialogLoading = true

      const cells = this.sheet.cells || []

      const postProcess = (header, columnIdx, row) => {
        const defaultVal = header.$cell.description || ''
        if (
          header.$cell.type === SheetCellTypes.drop_list
        && cells[columnIdx + 1]?.type === SheetCellTypes.schedule
        ) {
        // Найден заголовок с выбором даты, а за ним - ячейка с расписание
        // Ну и костыль!

          let date = row[columnIdx]
          if (!date || typeof date !== 'string') {
            return defaultVal
          }
          date = date.split('.').reverse().join('-')
          if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            return defaultVal
          }

          if (this.scheduleInformation && this.scheduleInformation.data) {
            const info = this.scheduleInformation.data.find((el) => el.date === date)
            if (info) {
              const { limit, count } = info
              const diff = (+limit ?? 0) - (+count ?? -1)
              if (diff > 0) {
                return `Свободных мест на эту дату: ${diff}`
              }
            }
          }
        }
        return defaultVal
      }

      const propsData = {
        headers: this.headers,
        data: item,
        index,
        all_cells: this.all_cells,
        disabled: this.readonly,
        verticalLineData: this.verticalLineData,
        verticalCellsData: this.verticalCellsData,
        headerDescriptionProcess: postProcess,
        onSave: this.onSaveItem,
        scheduleInformation: this.scheduleInformation ? this.scheduleInformation.data : [],
      }
      const modal = await createLKPublicationsFormdataTableEditModal(this, propsData)
      await modal.open()
      this.isEditDialogLoading = false
    },
    onDelete(index) {
      this.$emit('action', TableActionsTypes.delete, this.list[index])
    },
    onAdd() {
      this.$emit('action', TableActionsTypes.add)
    },
    onSaveItem(newData, index) {
      this.$emit('action', TableActionsTypes.edit, newData, { index })
    },
    async updateClusterizeObj() {
      await this.$nextTick()
      if (this.clusterizeObj) {
        this.clusterizeObj.update(this.list.map(this.renderRowData))
        this.clusterizeObj.refresh(true)
      }
      this.$emit('table:updated')
    },
    refreshClusteriseObj() {
      if (this.clusterizeObj) {
        this.clusterizeObj.refresh(true)
      }
    },
    async updateClusterizeErrors() {
      await this.$nextTick()
      if (this.clusterizeObj) {
        this.clusterizeObj.update(this.list.map(this.renderRowData))
        this.clusterizeObj.refresh(true)
      }
    },
  },
}
</script>

<style lang="scss">
$background-color: #ebf1fd;

// stylelint-disable no-descending-specificity
::-webkit-scrollbar:horizontal {
  height: 12px;
}

::-webkit-scrollbar:vertical {
  width: 12px;
}

.LKPublicationsDataCollectTable {
  table {
    padding: 0 !important;
  }

  tbody {
    position: sticky;
    z-index: 4;
    width: 100%;
    background-color: $webflow-color-white;
  }

  .v-data-table__wrapper {
    display: table;
    width: 100%;

    tr th {
      min-width: 10rem;
      color: $webflow-color-blue-dark-3 !important;
      background-color: $webflow-color-blue-light-3 !important;
    }

    tbody tr:nth-of-type(even) td,
    tbody tr:nth-of-type(even):hover td {
      background-color: $webflow-color-blue-light-4;
    }

    tbody tr:nth-of-type(odd):hover td {
      background-color: $webflow-color-white;
    }

    th,
    td {
      border: 1px solid $webflow-color-blue-light !important;
      border-left-width: 0 !important;

      &:last-of-type {
        border-right-width: 0 !important;
      }
    }

    .v-data-table-header th {
      font-size: 1rem;
      font-weight: 500;
      line-height: 1.6rem;
      text-align: center;

      &.orientation-vertical span {
        vertical-align: middle;
        writing-mode: vertical-lr;
        transform: rotate(180deg);
      }
    }

    td {
      font-size: 0.875rem;
      line-height: 1.4rem;
      border-top-width: 0 !important;

      &.focused {
        $outline: 0;

        border-color: $webflow-color-blue-dark !important;
        border-width: 2px !important;
        outline: $outline;
      }
    }
  }

  .is-cell {
    margin: -4px -7px;
  }

  & td#{&}__top {
    min-width: 120px;
    border-top-width: 1px !important;
  }

  &__header-left {
    position: sticky;
    left: 0;
    z-index: 8;
    min-width: 200px;
    max-width: 30%;
    text-align: left;
  }
}

.LKPublicationsDataCollectsForm__inner.clusterize-scroll {
  max-height: 60vh;
  overflow-x: scroll;

  .LKPublicationsDataCollectTable {
    display: table;
  }
}

.LKPublicationsDataCollectsForm__inner.expanded {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  max-height: unset;
  background-color: $background-color;
}

.collapse-wrapper {
  position: fixed;
  top: 29px;
  right: 32px;

  @import "~@/assets/scss/components/_LKHeaderButtons.scss";

  z-index: 10;
}

#LKPublicationsFormdataTable__contentArea.is-dialog-loading tr td {
  cursor: progress;
}

.LKPublicationsFormdataTable__col.error--text {
  &::after {
    $font-family: "Material Design Icons",
      sans-serif;

    display: inline-block;
    float: right;
    font-family: $font-family;
    font-size: 1.25rem;
    line-height: inherit;
    content: "\F05D6";
    text-rendering: auto;
    -webkit-font-smoothing: antialiased;
  }
}

.LKPublicationsFormdataTable__error-tooltip {
  z-index: 999999;
}

.LKPublicationsFormdataTable__action-button--add {
  margin-right: 0.5rem;
}

.LKPublicationsFormdataTable__action-button--add > *,
.LKPublicationsFormdataTable__action-button--delete > * {
  pointer-events: none;
}

.LKPublicationsFormdataTable__row.editable {
  cursor: pointer;

  &:hover .LKPublicationsFormdataTable__col {
    background-color: $webflow-color-blue-light-2 !important;
  }
}

.LKPublicationsFormdataTable__cell {
  display: -webkit-box;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  word-break: break-word;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.4;
}
</style>
